import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus, Copy, Edit3, Send, Archive, Lock, Save, Trash2, RotateCcw, AlertTriangle, MinusCircle, ClipboardCheck, Gauge } from 'lucide-react';
import useEhCompacto from '../../../utils/useEhCompacto';

// =====================================================================
// Construtor de versões do template de certificação (Módulo Manutenção).
// Espelha o construtor da Auditoria (ChecklistVersoes.js), adaptado ao modelo:
//   Equipamento (Fritadeira) → Versão → Sistema (PESO) → Componente (BLOQUEANTE)
// O PESO mora no sistema; o flag BLOQUEANTE (Crítico ⇒ não certifica) no componente.
//
// A versão também define os TESTES OBRIGATÓRIOS da Etapa 2 (2026-07-22). Antes
// eles eram fixos no código (6 testes de fritadeira); a chapa tem os seus, bem
// diferentes. Versão SEM testes cadastrados cai no comportamento antigo.
// =====================================================================

const API_BASE = '/api/manutencao/versoes';

const statusStyle = (s) => {
  if (s === 'Publicado') return { bg: '#d1fae5', color: '#065f46' };
  if (s === 'Rascunho')  return { bg: '#fef3c7', color: '#92400e' };
  return { bg: '#e2e8f0', color: '#475569' }; // Arquivado
};

const btn = (bg, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 10px', background: bg, color, border: 'none',
  borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer',
});

const token = () => localStorage.getItem('authToken');
const auth = () => ({ Authorization: `Bearer ${token()}` });

const CertificacaoVersoes = ({ onVoltar }) => {
  const ehCompacto = useEhCompacto();
  const [versoes, setVersoes] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [editando, setEditando] = useState(null);     // ID em edição
  const [mostrarNova, setMostrarNova] = useState(false);

  // Estado do formulário "Nova versão"
  const [novoEquipId, setNovoEquipId] = useState('');   // '' = escolher; '__novo__' = criar
  const [novoEquipNome, setNovoEquipNome] = useState('');
  const [novoEquipPrefixo, setNovoEquipPrefixo] = useState(''); // prefixo do código (ex.: FRT)
  const [novoNomeVersao, setNovoNomeVersao] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const [rv, re] = await Promise.all([
        fetch(API_BASE, { headers: auth() }),
        fetch(`${API_BASE}/equipamentos`, { headers: auth() }),
      ]);
      if (!rv.ok) throw new Error(`HTTP ${rv.status}`);
      setVersoes(await rv.json());
      setEquipamentos(re.ok ? await re.json() : []);
    } catch (e) {
      setErro('Erro ao carregar versões.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const criarVersao = async () => {
    if (!novoNomeVersao.trim()) { setErro('Informe o nome da versão.'); return; }
    const usandoNovo = novoEquipId === '__novo__';
    if (!usandoNovo && !novoEquipId) { setErro('Escolha um equipamento.'); return; }
    if (usandoNovo && !novoEquipNome.trim()) { setErro('Informe o nome do novo equipamento.'); return; }
    const body = usandoNovo
      ? { novo_equipamento: novoEquipNome.trim(), prefixo_equipamento: novoEquipPrefixo.trim(), nome_versao: novoNomeVersao.trim() }
      : { id_equipamento: Number(novoEquipId), nome_versao: novoNomeVersao.trim() };
    const r = await fetch(API_BASE, {
      method: 'POST',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao criar.'); return; }
    setMostrarNova(false);
    setNovoEquipId(''); setNovoEquipNome(''); setNovoEquipPrefixo(''); setNovoNomeVersao('');
    await carregar();
    setEditando(data.id);
  };

  const duplicar = async (id) => {
    const nome = window.prompt('Nome da nova versão (cópia):');
    if (!nome?.trim()) return;
    const r = await fetch(`${API_BASE}/${id}/duplicar`, {
      method: 'POST',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ nome_versao: nome }),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao duplicar.'); return; }
    await carregar();
    setEditando(data.id);
  };

  const acao = async (id, rota, confirmacao) => {
    if (confirmacao && !window.confirm(confirmacao)) return;
    const r = await fetch(`${API_BASE}/${id}/${rota}`, { method: 'POST', headers: auth() });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro na operação.'); return; }
    carregar();
  };

  if (editando) {
    return <VersaoEditor id={editando} onSair={() => { setEditando(null); carregar(); }} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onVoltar} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Gerenciar Versões do Template</h3>
      </div>

      <button
        onClick={() => setMostrarNova((v) => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '10px 16px', background: '#4f46e5', color: '#fff',
          border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', marginBottom: 16,
        }}
      >
        <Plus size={16} /> Nova versão (em branco)
      </button>

      {mostrarNova && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: ehCompacto ? '1fr' : '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Equipamento</label>
              <select value={novoEquipId} onChange={(e) => setNovoEquipId(e.target.value)}
                style={{ width: '100%', padding: 9, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}>
                <option value="">— escolher —</option>
                {equipamentos.map((eq) => <option key={eq.ID} value={eq.ID}>{eq.NOME}</option>)}
                <option value="__novo__">+ Novo equipamento…</option>
              </select>
            </div>
            {novoEquipId === '__novo__' && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Nome do novo equipamento</label>
                <input type="text" value={novoEquipNome} placeholder="ex.: Forno"
                  onChange={(e) => setNovoEquipNome(e.target.value)}
                  style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
              </div>
            )}
            {novoEquipId === '__novo__' && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Prefixo do código <span style={{ fontWeight: 400 }}>(opcional)</span></label>
                {/* Vira o prefixo do código dos equipamentos deste tipo (ex.: FRT-7K2P).
                    Se ficar em branco, usa as 3 primeiras letras do nome. */}
                <input type="text" value={novoEquipPrefixo} placeholder="ex.: FRT" maxLength={5}
                  onChange={(e) => setNovoEquipPrefixo(e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6, textTransform: 'uppercase' }} />
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Nome da versão</label>
              <input type="text" value={novoNomeVersao} placeholder="ex.: v1 — Fritadeira" autoFocus
                onChange={(e) => setNovoNomeVersao(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') criarVersao(); }}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button onClick={criarVersao} style={btn('#4f46e5', '#fff')}>
              <Save size={14} /> Criar (Rascunho)
            </button>
            <button onClick={() => setMostrarNova(false)} style={btn('#f1f5f9', '#475569')}>Cancelar</button>
          </div>
        </div>
      )}

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}
      {carregando && <p style={{ color: '#64748b' }}>Carregando…</p>}

      <div style={{ display: 'grid', gap: 10 }}>
        {versoes.map((v) => {
          const st = statusStyle(v.STATUS);
          const imutavel = v.STATUS === 'Publicado' && v.USOS > 0;
          return (
            <div key={v.ID} style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16,
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 800,
                    background: '#e0e7ff', color: '#4338ca',
                  }}>{v.EQUIPAMENTO}</span>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>{v.NOME_VERSAO}</span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800,
                    background: st.bg, color: st.color,
                  }}>{v.STATUS}</span>
                  {imutavel && (
                    <span title="Versão imutável: já tem certificações" style={{ color: '#64748b' }}>
                      <Lock size={14} />
                    </span>
                  )}
                </div>
                {v.DESCRICAO && <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{v.DESCRICAO}</div>}
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Certificações: {v.USOS}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setEditando(v.ID)} title={imutavel ? 'Visualizar (imutável)' : 'Editar'}
                  style={btn('#eef2ff', '#4338ca')}>
                  <Edit3 size={14} /> {imutavel ? 'Ver' : 'Editar'}
                </button>
                <button onClick={() => duplicar(v.ID)} title="Duplicar" style={btn('#eff6ff', '#1d4ed8')}>
                  <Copy size={14} /> Duplicar
                </button>
                {v.STATUS === 'Rascunho' && (
                  <button onClick={() => acao(v.ID, 'publicar', 'Publicar esta versão? Após a primeira certificação ela ficará imutável.')} style={btn('#d1fae5', '#065f46')}>
                    <Send size={14} /> Publicar
                  </button>
                )}
                {v.STATUS === 'Publicado' && (
                  <button onClick={() => acao(v.ID, 'arquivar', 'Arquivar esta versão? Ela some das listas mas as certificações já feitas continuam preservadas.')} style={btn('#f1f5f9', '#475569')}>
                    <Archive size={14} /> Arquivar
                  </button>
                )}
                {v.STATUS === 'Arquivado' && (
                  <button onClick={() => acao(v.ID, 'republicar', 'Republicar esta versão? Ela volta a aparecer na lista para preenchimento.')} style={btn('#d1fae5', '#065f46')}>
                    <RotateCcw size={14} /> Republicar
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Editor da versão (sistemas + componentes), no mesmo arquivo.
// ---------------------------------------------------------------------
const VersaoEditor = ({ id, onSair }) => {
  const ehCompacto = useEhCompacto();
  const [dados, setDados] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const r = await fetch(`${API_BASE}/${id}`, { headers: auth() });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao carregar.');
      setDados({
        nome_versao: data.versao.NOME_VERSAO,
        descricao: data.versao.DESCRICAO || '',
        equipamento: data.versao.EQUIPAMENTO,
        status: data.versao.STATUS,
        podeEditar: data.podeEditar,
        usos: data.usos,
        sistemas: (data.sistemas || []).map((s) => ({
          id: s.ID, nome: s.NOME, peso: s.PESO, ordem: s.ORDEM,
          componentes: (s.componentes || []).map((c) => ({
            id: c.ID, nome: c.NOME, bloqueante: !!c.BLOQUEANTE, permite_na: !!c.PERMITE_NA, ordem: c.ORDEM,
          })),
        })),
        // Testes obrigatórios da Etapa 2. Lista vazia é legítima (fritadeira).
        testes: (data.testes || []).map((t) => ({
          id: t.ID, nome: t.TESTE, permite_na: !!t.PERMITE_NA, ordem: t.ORDEM,
        })),
        // Medições com valor de referência. Vazio = bloco único de temperatura.
        medicoes: (data.medicoes || []).map((m) => ({
          id: m.ID, nome: m.MEDICAO, unidade: m.UNIDADE || '',
          valor_referencia: m.VALOR_REFERENCIA ?? '',
          valor_referencia_max: m.VALOR_REFERENCIA_MAX ?? '',
          permite_na: !!m.PERMITE_NA, ordem: m.ORDEM,
        })),
      });
    } catch (e) {
      setErro(e.message);
    }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  if (!dados) return <p style={{ color: '#64748b' }}>Carregando…</p>;

  const ro = !dados.podeEditar;

  const atualizarSistema = (i, campo, valor) => {
    setDados((d) => {
      const novos = [...d.sistemas];
      novos[i] = { ...novos[i], [campo]: campo === 'peso' ? Number(valor) || 0 : valor };
      return { ...d, sistemas: novos };
    });
  };
  const adicionarSistema = () => {
    setDados((d) => ({ ...d, sistemas: [...d.sistemas, { nome: 'Novo Sistema', peso: 1, ordem: d.sistemas.length + 1, componentes: [] }] }));
  };
  const removerSistema = (i) => {
    setDados((d) => ({ ...d, sistemas: d.sistemas.filter((_, idx) => idx !== i) }));
  };
  const adicionarComponente = (i) => {
    setDados((d) => {
      const novos = [...d.sistemas];
      novos[i] = { ...novos[i], componentes: [...novos[i].componentes, { nome: '', bloqueante: false, permite_na: false, ordem: novos[i].componentes.length + 1 }] };
      return { ...d, sistemas: novos };
    });
  };
  const atualizarComponente = (si, ci, campo, valor) => {
    setDados((d) => {
      const novos = [...d.sistemas];
      const cs = [...novos[si].componentes];
      cs[ci] = { ...cs[ci], [campo]: valor };
      novos[si] = { ...novos[si], componentes: cs };
      return { ...d, sistemas: novos };
    });
  };
  const removerComponente = (si, ci) => {
    setDados((d) => {
      const novos = [...d.sistemas];
      novos[si] = { ...novos[si], componentes: novos[si].componentes.filter((_, i) => i !== ci) };
      return { ...d, sistemas: novos };
    });
  };

  // --- Testes obrigatórios da Etapa 2 (lista plana, sem agrupamento) ---
  const adicionarTeste = () => {
    setDados((d) => ({ ...d, testes: [...d.testes, { nome: '', permite_na: false, ordem: d.testes.length + 1 }] }));
  };
  const atualizarTeste = (i, campo, valor) => {
    setDados((d) => {
      const novos = [...d.testes];
      novos[i] = { ...novos[i], [campo]: valor };
      return { ...d, testes: novos };
    });
  };
  const removerTeste = (i) => {
    setDados((d) => ({ ...d, testes: d.testes.filter((_, idx) => idx !== i) }));
  };

  // --- Medições da Etapa 2 (valor medido em campo × referência do fabricante) ---
  const adicionarMedicao = () => {
    setDados((d) => ({
      ...d,
      medicoes: [...d.medicoes, { nome: '', unidade: '', valor_referencia: '', valor_referencia_max: '', permite_na: false, ordem: d.medicoes.length + 1 }],
    }));
  };
  const atualizarMedicao = (i, campo, valor) => {
    setDados((d) => {
      const novas = [...d.medicoes];
      novas[i] = { ...novas[i], [campo]: valor };
      return { ...d, medicoes: novas };
    });
  };
  const removerMedicao = (i) => {
    setDados((d) => ({ ...d, medicoes: d.medicoes.filter((_, idx) => idx !== i) }));
  };

  const salvar = async () => {
    setSalvando(true); setErro(''); setSucesso('');
    try {
      const r = await fetch(`${API_BASE}/${id}`, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome_versao: dados.nome_versao,
          descricao: dados.descricao,
          sistemas: dados.sistemas.map((s, si) => ({
            nome: s.nome, peso: Number(s.peso) || 1, ordem: si + 1,
            componentes: s.componentes.map((c, ci) => ({
              nome: c.nome, bloqueante: c.bloqueante ? 1 : 0, permite_na: c.permite_na ? 1 : 0, ordem: ci + 1,
            })),
          })),
          // Testes vazios (sem nome) são descartados no backend. Mandar a lista
          // sempre — omitir o campo PRESERVA a lista atual, e aí apagar o último
          // teste da tela nunca chegaria a gravar.
          testes: dados.testes.map((t, ti) => ({
            nome: t.nome, permite_na: t.permite_na ? 1 : 0, ordem: ti + 1,
          })),
          medicoes: dados.medicoes.map((m, mi) => ({
            nome: m.nome, unidade: m.unidade || null,
            valor_referencia: m.valor_referencia, valor_referencia_max: m.valor_referencia_max,
            permite_na: m.permite_na ? 1 : 0, ordem: mi + 1,
          })),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao salvar.');
      setSucesso('Estrutura salva.');
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const totalComponentes = dados.sistemas.reduce((acc, s) => acc + s.componentes.length, 0);
  const totalBloqueantes = dados.sistemas.reduce((acc, s) => acc + s.componentes.filter((c) => c.bloqueante).length, 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onSair} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          {dados.equipamento} · {dados.nome_versao}
        </h3>
      </div>

      {ro && (
        <div style={{
          padding: 12, background: '#fef3c7', color: '#92400e', borderRadius: 8, marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Lock size={16} /> Versão imutável: já tem {dados.usos} certificação(ões) gravada(s). Para modificar, use "Duplicar".
        </div>
      )}
      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}
      {sucesso && <div style={{ padding: 12, background: '#d1fae5', color: '#065f46', borderRadius: 8, marginBottom: 12 }}>{sucesso}</div>}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: ehCompacto ? '1fr' : '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Nome da versão</label>
            <input type="text" disabled={ro} value={dados.nome_versao}
              onChange={(e) => setDados({ ...dados, nome_versao: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Descrição</label>
            <input type="text" disabled={ro} value={dados.descricao}
              onChange={(e) => setDados({ ...dados, descricao: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 13, color: '#475569' }}>
          Status: <strong>{dados.status}</strong> · Sistemas: <strong>{dados.sistemas.length}</strong> ·
          {' '}Componentes: <strong>{totalComponentes}</strong> · Bloqueantes: <strong>{totalBloqueantes}</strong> ·
          {' '}Testes da Etapa 2: <strong>{dados.testes.length || 'padrão da fritadeira'}</strong> ·
          {' '}Medições: <strong>{dados.medicoes.length || 'só calibração de temperatura'}</strong>
        </div>
      </div>

      {dados.sistemas.map((s, si) => (
        <div key={si} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <input type="text" disabled={ro} value={s.nome}
              onChange={(e) => atualizarSistema(si, 'nome', e.target.value)}
              placeholder="Nome do sistema"
              style={{ flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontWeight: 700 }} />
            <label title="Peso do sistema (todo componente herda este peso)"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>
              Peso
              <input type="number" disabled={ro} value={s.peso} min={1}
                onChange={(e) => atualizarSistema(si, 'peso', e.target.value)}
                style={{ width: 64, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
            </label>
            {!ro && (
              <button onClick={() => removerSistema(si)} style={btn('#fee2e2', '#991b1b')} title="Remover sistema">
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {s.componentes.map((c, ci) => (
              <div key={ci} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="text" disabled={ro} value={c.nome} placeholder="Nome do componente"
                  onChange={(e) => atualizarComponente(si, ci, 'nome', e.target.value)}
                  style={{ flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                {/* Bloqueante: se este componente for Crítico na auditoria, o
                    equipamento NÃO certifica, independente do Índice de Saúde. */}
                <label title="Crítico neste componente reprova a certificação (bloqueante)"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: c.bloqueante ? '#b91c1c' : '#475569', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" disabled={ro} checked={!!c.bloqueante}
                    onChange={(e) => atualizarComponente(si, ci, 'bloqueante', e.target.checked)} />
                  <AlertTriangle size={13} /> Bloqueante
                </label>
                {/* Permite N/A: o componente pode ser marcado "Não Aplicável" no
                    diagnóstico (sai do cálculo do Índice de Saúde) — ex.: cubas
                    que não existem em toda fritadeira. */}
                <label title="Permite marcar N/A (Não Aplicável) no diagnóstico — sai do cálculo do Índice de Saúde"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: c.permite_na ? '#4338ca' : '#475569', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" disabled={ro} checked={!!c.permite_na}
                    onChange={(e) => atualizarComponente(si, ci, 'permite_na', e.target.checked)} />
                  <MinusCircle size={13} /> Permite N/A
                </label>
                {!ro && (
                  <button onClick={() => removerComponente(si, ci)} style={btn('#fee2e2', '#991b1b')} title="Remover">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
            {!ro && (
              <button onClick={() => adicionarComponente(si)} style={btn('#eff6ff', '#1d4ed8')}>
                <Plus size={12} /> Componente
              </button>
            )}
          </div>
        </div>
      ))}

      {/* ---------------------------------------------------------------
          Testes obrigatórios da Etapa 2. Lista plana (sem sistema/peso):
          não entram no Índice de Saúde — são um portão. Qualquer um
          reprovado derruba a certificação, independente do %.
          --------------------------------------------------------------- */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <ClipboardCheck size={16} color="#0f172a" />
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
            Testes obrigatórios da Etapa 2
          </h4>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
          Verificações feitas <strong>depois do reparo</strong>, na certificação. Não entram no
          Índice de Saúde: qualquer um reprovado <strong>impede a certificação</strong>, mesmo com
          índice alto.
          {dados.testes.length === 0 && (
            <>
              {' '}Nenhum cadastrado — esta versão usa a <strong>lista padrão da fritadeira</strong>
              {' '}(Aquecimento, Recuperação térmica, Sistema de filtragem, Segurança elétrica,
              {' '}Vazamentos, Operação Real, mais um teste por cuba avaliada).
            </>
          )}
        </p>
        <div style={{ display: 'grid', gap: 6 }}>
          {dados.testes.map((t, ti) => (
            <div key={ti} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', width: 22, textAlign: 'right' }}>{ti + 1}.</span>
              <input type="text" disabled={ro} value={t.nome} placeholder="Nome do teste (ex.: GAP 10:1 de todos os platens)"
                onChange={(e) => atualizarTeste(ti, 'nome', e.target.value)}
                style={{ flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
              {/* N/A no teste: usado quando ele não se aplica ao equipamento
                  concreto — ex.: "Ausência de vazamento de gás" numa chapa
                  elétrica. N/A sai da checagem "todos aprovados". */}
              <label title="Permite marcar N/A quando o teste não se aplica ao equipamento (ex.: gás numa chapa elétrica)"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: t.permite_na ? '#4338ca' : '#475569', whiteSpace: 'nowrap' }}>
                <input type="checkbox" disabled={ro} checked={!!t.permite_na}
                  onChange={(e) => atualizarTeste(ti, 'permite_na', e.target.checked)} />
                <MinusCircle size={13} /> Permite N/A
              </label>
              {!ro && (
                <button onClick={() => removerTeste(ti)} style={btn('#fee2e2', '#991b1b')} title="Remover teste">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
          {!ro && (
            <button onClick={adicionarTeste} style={btn('#eff6ff', '#1d4ed8')}>
              <Plus size={12} /> Teste
            </button>
          )}
        </div>
      </div>

      {/* ---------------------------------------------------------------
          Medições da Etapa 2: valor medido em campo contra a referência do
          fabricante. Ficam no template (e não digitadas na hora) para que a
          referência seja igual em toda loja e dê para comparar certificações.
          --------------------------------------------------------------- */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Gauge size={16} color="#0f172a" />
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
            Medições da Etapa 2
          </h4>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
          Valores medidos em campo (temperatura, GAP, pressão…) comparados com a
          referência. Deixe o <strong>máximo</strong> em branco quando o alvo for um valor único;
          preencha os dois quando o aceitável for uma faixa (ex.: 6 a 8 elevações).
          {dados.medicoes.length === 0 && (
            <> Nenhuma cadastrada — esta versão usa o <strong>bloco único de calibração de
              temperatura</strong> da fritadeira.</>
          )}
        </p>
        <div style={{ display: 'grid', gap: 6 }}>
          {dados.medicoes.map((m, mi) => (
            <div key={mi} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', width: 22, textAlign: 'right' }}>{mi + 1}.</span>
              <input type="text" disabled={ro} value={m.nome} placeholder="Medição (ex.: Platen ESQUERDO · Temperatura superior)"
                onChange={(e) => atualizarMedicao(mi, 'nome', e.target.value)}
                style={{ flex: '1 1 260px', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
              <input type="text" disabled={ro} value={m.unidade} placeholder="un."
                title="Unidade: °C, psi, mm, s, ciclos"
                onChange={(e) => atualizarMedicao(mi, 'unidade', e.target.value)}
                style={{ width: 64, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
              <input type="number" disabled={ro} value={m.valor_referencia} placeholder="ref."
                title="Valor de referência (ou início da faixa)"
                onChange={(e) => atualizarMedicao(mi, 'valor_referencia', e.target.value)}
                style={{ width: 80, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>a</span>
              <input type="number" disabled={ro} value={m.valor_referencia_max} placeholder="máx."
                title="Fim da faixa — deixe vazio se o alvo for um valor único"
                onChange={(e) => atualizarMedicao(mi, 'valor_referencia_max', e.target.value)}
                style={{ width: 80, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
              <label title="Permite N/A quando a medição não se aplica (ex.: platen inexistente)"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: m.permite_na ? '#4338ca' : '#475569', whiteSpace: 'nowrap' }}>
                <input type="checkbox" disabled={ro} checked={!!m.permite_na}
                  onChange={(e) => atualizarMedicao(mi, 'permite_na', e.target.checked)} />
                <MinusCircle size={13} /> N/A
              </label>
              {!ro && (
                <button onClick={() => removerMedicao(mi)} style={btn('#fee2e2', '#991b1b')} title="Remover medição">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
          {!ro && (
            <button onClick={adicionarMedicao} style={btn('#eff6ff', '#1d4ed8')}>
              <Plus size={12} /> Medição
            </button>
          )}
        </div>
      </div>

      {!ro && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={adicionarSistema} style={{
            padding: '10px 16px', background: '#f1f5f9', color: '#475569',
            border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}>
            <Plus size={14} /> Sistema
          </button>
          <button onClick={salvar} disabled={salvando} style={{
            padding: '10px 20px', background: salvando ? '#94a3b8' : '#4f46e5', color: '#fff',
            border: 'none', borderRadius: 8, fontWeight: 800, cursor: salvando ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <Save size={14} /> {salvando ? 'Salvando…' : 'Salvar estrutura'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CertificacaoVersoes;
